import React, { useMemo } from 'react';
import { Box, FeeLabel, Inscription, List } from '@common-fe/common-fe';

import PaymentTypeIcon from '@/components/elements/PaymentTypeIcon';
import PaymentMethodDescription from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately/PaymentMethodLabel/PaymentMethodDescription';
import { PaymentMethod } from '@/modules/transaction/components/ReimburseMe/types/payment.types';

import { getPaymentMethodDetails } from './getPaymentMethodName';
import PaymentTypeIconWrapper from './PaymentTypeIconWrapper';
import { PlaidDirectDepositBanner } from './PlaidDirectDepositBanner';

export interface PaymentMethodOption {
  name: string;
  onClick: (paymentMethodId: string) => void;
  isWarning?: boolean;
  optionStyle?: React.CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isAvailable?: (fields: any, paymentMethodId: string) => boolean;
}

interface Props {
  paymentMethod: PaymentMethod;
  options: PaymentMethodOption[];
  employeeFee?: string;
  isBannerShown?: boolean;
  isHighlighted?: boolean;
  refetchPaymentMethods: () => void;
}

const MobilePaymentMethodCard: React.FC<Props> = ({
  paymentMethod,
  options,
  employeeFee,
  isBannerShown,
  isHighlighted,
  refetchPaymentMethods,
}) => {
  const details = useMemo(() => getPaymentMethodDetails(paymentMethod), [paymentMethod]);
  const availableOptions = useMemo(
    () =>
      options.filter((option) =>
        option.isAvailable ? option.isAvailable([], paymentMethod?.id || '') : true
      ),
    [options, paymentMethod]
  );

  return (
    <Box
      data-testid={`MobilePaymentMethodCard_${paymentMethod?.id}`}
      round="container2Round"
      background="canvas"
      border={{ size: isHighlighted ? '2px' : 'small', color: isHighlighted ? 'accentActive' : 'border2' }}
      pad="spacing16"
      margin={{ bottom: 'spacing8' }}
    >
      <Box direction="row" align="start" justify="between">
        <Box direction="row" align="start" flex>
          <PaymentTypeIconWrapper>
            <PaymentTypeIcon paymentType={paymentMethod?.type} />
          </PaymentTypeIconWrapper>
          <Box margin={{ left: 'spacing8' }} flex>
            <Inscription size="16px" lineHeight="22px" color="textBody">
              {details?.typeName}
            </Inscription>
            <Box direction="row" align="center" gap="spacing8" margin={{ top: 'spacing4' }} wrap>
              <FeeLabel amount={employeeFee} testId={paymentMethod?.type} />
              {paymentMethod?.isDefault ? (
                <Box
                  data-testid={`MobilePaymentMethodCard_${paymentMethod?.id}_default`}
                  background="legend"
                  round="checkboxRound"
                  pad={{ horizontal: 'spacing8', vertical: 'spacing2' }}
                >
                  <Inscription size="12px" color="textBody" weight={500} lineHeight="18px">
                    Default
                  </Inscription>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>

        {availableOptions.length ? (
          <Box margin={{ left: 'spacing8' }}>
            <List.Item.Options testId={`payment_method_${paymentMethod?.id}`}>
              {availableOptions.map((option) => (
                <List.Item.Options.Item
                  key={option.name}
                  data-testid={`MobilePaymentMethodCard_${paymentMethod?.id}_option_${option.name}`}
                  onClick={() => option.onClick(paymentMethod?.id || '')}
                  isDanger={option.isWarning}
                >
                  <Inscription style={option.optionStyle}>{option.name}</Inscription>
                </List.Item.Options.Item>
              ))}
            </List.Item.Options>
          </Box>
        ) : null}
      </Box>

      <Box margin={{ top: 'spacing8' }}>
        <PaymentMethodDescription paymentMethod={paymentMethod} />
      </Box>

      {isBannerShown ? (
        <Box margin={{ top: 'spacing12' }}>
          <PlaidDirectDepositBanner
            status={paymentMethod?.verificationStatus}
            accountId={paymentMethod?.accountId}
            refetch={refetchPaymentMethods}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default MobilePaymentMethodCard;
